import type { ProductListAPIResponse } from '@partful/shared-types';
import { useProductInventoryQuery } from '@partful/shared/lib/hooks/useProductInventoryQuery';
import { Job } from '@providers/job/types';
import { useTenant } from '@providers/Tenant';
import { productInventoryEndpoints } from '@services/productInventory';
import { usePermissions } from 'src/hooks/usePermissions';

// a type that extracts the specific Job type based on jobType
type JobMap = {
  [J in Job as J['jobType']]: J;
};

export const useProductLibraryQuery = <T extends keyof JobMap | undefined>(jobType?: T) => {
  const { accountId } = useTenant();
  const canAddProducts = usePermissions(['add', 'product']);
  const canViewDraft = usePermissions(['view', 'draft']);

  const { data: configurations, isLoading: isConfigLoading } = useProductInventoryQuery<ProductListAPIResponse>(
    productInventoryEndpoints.configurations,
    {
      account: accountId,
      status: canViewDraft ? 'DRAFT,LIVE' : 'LIVE',
    },
    {
      refetchOnMount: true,
    }
  );

  // This query will return `Job[]` if no jobType is passed,  or an array of the requested jobType
  // if it is passed
  const { data: jobs, isFetching: isJobsLoading } = useProductInventoryQuery<
    T extends keyof JobMap ? JobMap[T][] : Job[]
  >(
    productInventoryEndpoints.contentProcessing.root,
    {
      account: accountId,
      ...(jobType ? { jobType } : {}), // Include jobType if provided
    },
    {
      refetchOnMount: true,
      enabled: canAddProducts,
    }
  );

  return {
    configurations: configurations?.map((configuration) => ({
      ...configuration,
      productId: configuration.productExternalID,
    })) as ProductListAPIResponse,
    jobs: jobs ?? [],
    isLoading: isConfigLoading || (isJobsLoading && canAddProducts),
  };
};
